'use client'

import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import { Client, getClient } from '@/lib/api/clients/client-api'
import {
    Allergy,
    getAllergiesByClient,
} from '@/lib/api/prontuario/prontuario-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import AlergiasPage from '@/pages/admin/medical/prontuario/AlergiasPage'
import AnamneseDetailPage from '@/pages/admin/medical/prontuario/AnamneseDetailPage'
import AnamneseFormPage from '@/pages/admin/medical/prontuario/AnamneseFormPage'
import AnamnesesPage from '@/pages/admin/medical/prontuario/AnamnesesPage'
import AnotacoesPage from '@/pages/admin/medical/prontuario/AnotacoesPage'
import AntecedentesPage from '@/pages/admin/medical/prontuario/AntecedentesPage'
import AtestadosPage from '@/pages/admin/medical/prontuario/AtestadosPage'
import ConsentimentosPage from '@/pages/admin/medical/prontuario/ConsentimentosPage'
import ConsultasPage from '@/pages/admin/medical/prontuario/ConsultasPage'
import ConveniosPage from '@/pages/admin/medical/prontuario/ConveniosPage'
import DiagnosticosPage from '@/pages/admin/medical/prontuario/DiagnosticosPage'
import EncaminhamentosPage from '@/pages/admin/medical/prontuario/EncaminhamentosPage'
import EvolucaoDetailPage from '@/pages/admin/medical/prontuario/EvolucaoDetailPage'
import EvolucaoFormPage from '@/pages/admin/medical/prontuario/EvolucaoFormPage'
import EvolucoesPage from '@/pages/admin/medical/prontuario/EvolucoesPage'
import ExamesPage from '@/pages/admin/medical/prontuario/ExamesPage'
import HealthMapPage from '@/pages/admin/medical/prontuario/HealthMapPage'
import ProcedimentosPage from '@/pages/admin/medical/prontuario/ProcedimentosPage'
import { ProntuarioDataProvider } from '@/pages/admin/medical/prontuario/prontuario-context'
import { PRONTUARIO_TABS } from '@/pages/admin/medical/prontuario/prontuario-tabs'
import ProntuarioResumoPage from '@/pages/admin/medical/prontuario/ProntuarioResumoPage'
import ReceituarioPage from '@/pages/admin/medical/prontuario/ReceituarioPage'
import TimelinePage from '@/pages/admin/medical/prontuario/TimelinePage'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBook,
    MdClose,
    MdOpenInNew,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

/** Aba -> conteúdo. */
const TAB_CONTENT: Record<string, React.ComponentType> = {
    resumo: ProntuarioResumoPage,
    alergias: AlergiasPage,
    antecedentes: AntecedentesPage,
    anamneses: AnamnesesPage,
    evolucoes: EvolucoesPage,
    consultas: ConsultasPage,
    exames: ExamesPage,
    procedimentos: ProcedimentosPage,
    diagnosticos: DiagnosticosPage,
    encaminhamentos: EncaminhamentosPage,
    receituario: ReceituarioPage,
    atestados: AtestadosPage,
    convenios: ConveniosPage,
    consentimentos: ConsentimentosPage,
    'health-map': HealthMapPage,
    anotacoes: AnotacoesPage,
    timeline: TimelinePage,
}

interface ModalRoute {
    /** Caminho relativo a `/person/clients/:clientId/prontuario`. */
    pattern: RegExp
    component: React.ComponentType
    /** Nome do parâmetro capturado, na ordem dos grupos da regex. */
    paramKeys?: string[]
    title: string
}

/**
 * Telas do prontuário que NÃO são abas — os formulários e detalhes alcançados de
 * dentro delas ("Nova evolução", "ver anamnese"). Precisam existir aqui, senão o
 * clique tiraria o médico da consulta que ele está atendendo.
 */
const MODAL_ROUTES: ModalRoute[] = [
    {
        pattern: /^\/evolucoes\/new$/,
        component: EvolucaoFormPage,
        title: 'Nova evolução',
    },
    {
        pattern: /^\/evolucoes\/(\d+)\/editar$/,
        component: EvolucaoFormPage,
        paramKeys: ['evolucaoId'],
        title: 'Editar evolução',
    },
    {
        pattern: /^\/evolucoes\/(\d+)$/,
        component: EvolucaoDetailPage,
        paramKeys: ['evolucaoId'],
        title: 'Evolução',
    },
    {
        pattern: /^\/anamneses\/new$/,
        component: AnamneseFormPage,
        title: 'Nova anamnese',
    },
    {
        pattern: /^\/anamneses\/(\d+)\/editar$/,
        component: AnamneseFormPage,
        paramKeys: ['anamneseId'],
        title: 'Editar anamnese',
    },
    {
        pattern: /^\/anamneses\/(\d+)$/,
        component: AnamneseDetailPage,
        paramKeys: ['anamneseId'],
        title: 'Anamnese',
    },
]

interface ResolvedRoute {
    path: string
    title: string
    component: React.ComponentType
    params: Record<string, string | undefined>
}

/** Resolve um caminho relativo do prontuário para a tela que o modal deve mostrar. */
function resolveRoute(relativePath: string): ResolvedRoute | null {
    const path = relativePath === '/' ? '' : relativePath

    const tab = PRONTUARIO_TABS.find(t => t.path === path)
    if (tab && TAB_CONTENT[tab.id]) {
        return {
            path,
            title: tab.label,
            component: TAB_CONTENT[tab.id],
            params: {},
        }
    }

    for (const route of MODAL_ROUTES) {
        const match = path.match(route.pattern)
        if (!match) continue

        const params: Record<string, string | undefined> = {}
        route.paramKeys?.forEach((key, index) => {
            params[key] = match[index + 1]
        })
        return {
            path,
            title: route.title,
            component: route.component,
            params,
        }
    }

    return null
}

interface Props {
    /** Paciente do atendimento. Sem ele o menu não aparece. */
    clientId?: number
    clientName?: string
}

/**
 * Menu "Prontuário" das telas de atendimento (consulta, exame, procedimento).
 *
 * Cada item é uma aba do prontuário e abre em modal, sobre a tela do atendimento:
 * o médico não pode sair da consulta que está atendendo para olhar uma alergia, uma
 * evolução ou um convênio — e sair e voltar custa duas navegações e o contexto do
 * que estava fazendo.
 *
 * Só existe no segmento de saúde: fora dele não há prontuário eletrônico. O
 * conteúdo é a MESMA página da rota do prontuário — nada é reescrito aqui, e aba
 * nova aparece no menu sozinha (ver `prontuario-tabs.ts`).
 */
export default function ProntuarioMenu({ clientId, clientName }: Props) {
    const { isHealth } = useServiceLabel()
    const [openTabId, setOpenTabId] = useState<string | null>(null)

    if (!isHealth || !clientId) return null

    const items: ActionMenuItem[] = PRONTUARIO_TABS.map(tab => {
        const TabIcon = tab.icon
        return {
            key: tab.id,
            label: tab.label,
            icon: <TabIcon />,
            onClick: () => setOpenTabId(tab.id),
        }
    })

    return (
        <>
            <ActionsMenu items={items} label="Prontuário" />
            {openTabId && (
                <ProntuarioTabModal
                    clientId={clientId}
                    clientName={clientName}
                    tabId={openTabId}
                    onClose={() => setOpenTabId(null)}
                />
            )}
        </>
    )
}

interface ModalProps {
    clientId: number
    clientName?: string
    tabId: string
    onClose: () => void
}

/**
 * Modal com o conteúdo de UMA aba do prontuário — a que foi escolhida no menu.
 *
 * Sem a régua de abas aqui dentro: o modal é uma consulta pontual ("ver as
 * alergias deste paciente"), não o prontuário inteiro sobreposto ao atendimento.
 *
 * As telas alcançadas de dentro da aba (nova evolução, ver anamnese) são resolvidas
 * AQUI, com uma pilha própria de navegação, em vez de virarem uma navegação de
 * verdade: sair da tela é justamente o que este menu existe para evitar.
 */
function ProntuarioTabModal({
    clientId,
    clientName,
    tabId,
    onClose,
}: ModalProps) {
    const routerNavigate = useNavigate()
    const { clientLabel } = useClientLabel()

    const [client, setClient] = useState<Client | null>(null)
    const [allergies, setAllergies] = useState<Allergy[]>([])
    const [loading, setLoading] = useState(true)

    const initialTab =
        PRONTUARIO_TABS.find(t => t.id === tabId) ?? PRONTUARIO_TABS[0]
    /** Pilha de caminhos relativos; o último é o que está na tela. */
    const [stack, setStack] = useState<string[]>([initialTab.path])

    const basePath = `/person/clients/${clientId}/prontuario`
    const currentPath = stack[stack.length - 1]
    const route = resolveRoute(currentPath) ?? {
        path: initialTab.path,
        title: initialTab.label,
        component: TAB_CONTENT[initialTab.id],
        params: {},
    }

    useEffect(() => {
        loadData()
    }, [clientId])

    const loadData = async () => {
        setLoading(true)
        try {
            const [clientResponse, allergiesResponse] = await Promise.all([
                getClient(clientId),
                getAllergiesByClient(clientId),
            ])

            if (clientResponse.success && clientResponse.result) {
                setClient(clientResponse.result)
            } else {
                toast.error(
                    clientResponse.error || `Erro ao carregar ${clientLabel}`
                )
                onClose()
                return
            }

            if (allergiesResponse.success) {
                setAllergies(allergiesResponse.result || [])
            }
        } catch (error) {
            toast.error(`Erro ao carregar dados do ${clientLabel}`)
            onClose()
        } finally {
            setLoading(false)
        }
    }

    /**
     * `navigate()` das telas de dentro do modal.
     *
     * Caminho do prontuário deste paciente empilha aqui mesmo. Qualquer outro
     * destino (o detalhe de um exame, a tela de agendamento) abre em NOVA ABA: o
     * atendimento em andamento continua onde estava.
     */
    const navigateInModal = (to: string | number) => {
        if (typeof to === 'number') {
            // Voltar: desempilha; se não há para onde voltar, fecha o modal.
            setStack(previous =>
                previous.length > 1 ? previous.slice(0, -1) : previous
            )
            if (stack.length <= 1) onClose()
            return
        }

        const [pathname] = to.split('?')
        if (pathname === basePath || pathname.startsWith(`${basePath}/`)) {
            const relative = to.slice(basePath.length).split('?')[0]
            if (resolveRoute(relative)) {
                setStack(previous => {
                    // Voltar para uma tela que já está na pilha (salvar a evolução
                    // devolve à lista) desempilha, em vez de empilhar de novo.
                    const existing = previous.indexOf(relative)
                    return existing >= 0
                        ? previous.slice(0, existing + 1)
                        : [...previous, relative]
                })
                return
            }
        }

        window.open(to, '_blank', 'noopener')
    }

    const activeAllergiesCount = allergies.filter(
        a => a.status === 'ACTIVE'
    ).length

    const TabContent = route.component

    return (
        /**
         * Overlay próprio, e NÃO `modal`/`modal-box` do DaisyUI: o `.modal-box` traz
         * `translate`/`scale`, e um ancestral com transform vira containing block —
         * os modais das próprias telas ("Nova alergia", "Novo diagnóstico"), que são
         * `position: fixed`, ficavam presos dentro desta caixa e cortados pelo
         * overflow dela.
         *
         * Pelo mesmo motivo o z-index é 900, ABAIXO do 999 que o DaisyUI dá ao
         * `.modal`: assim o modal da tela abre por cima deste, e não por baixo.
         */
        <div
            className="fixed inset-0 z-[900] flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            onClick={event => {
                // Só o clique no fundo fecha — clique dentro do conteúdo não.
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div className="w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden rounded-2xl border border-base-300/50 bg-base-100 shadow-xl">
                {/* Cabeçalho */}
                <div className="flex items-start justify-between gap-3 border-b border-base-300/60 px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                        {stack.length > 1 && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => navigateInModal(-1)}
                                aria-label="Voltar"
                                title="Voltar"
                            >
                                <MdArrowBack className="text-lg" />
                            </button>
                        )}
                        <div className="min-w-0">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <MdBook className="text-secondary" />
                                <span className="truncate">
                                    Prontuário
                                    {client?.name || clientName
                                        ? ` — ${client?.name || clientName}`
                                        : ''}
                                </span>
                                {activeAllergiesCount > 0 && (
                                    <span
                                        className="badge badge-error gap-1 text-white"
                                        title={`${activeAllergiesCount} alergia(s) ativa(s)`}
                                    >
                                        <MdWarning className="text-sm" />
                                        {activeAllergiesCount}
                                    </span>
                                )}
                            </h3>
                            <p className="text-sm text-base-content/60">
                                {route.title}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {/* Sair para o prontuário inteiro é decisão explícita do
                            operador — e leva junto a tela em que ele está. */}
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm gap-2"
                            onClick={() => routerNavigate(`${basePath}${route.path}`)}
                            title="Abrir o prontuário completo"
                        >
                            <MdOpenInNew className="text-base" />
                            <span className="hidden sm:inline">
                                Abrir prontuário
                            </span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-circle"
                            onClick={onClose}
                            aria-label="Fechar"
                        >
                            <MdClose />
                        </button>
                    </div>
                </div>

                {/* Conteúdo */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading || !client ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                            <p className="text-base-content/60 text-sm">
                                Carregando prontuário...
                            </p>
                        </div>
                    ) : (
                        <ProntuarioDataProvider
                            value={{
                                client,
                                allergies,
                                reloadAllergies: loadData,
                                navigate: navigateInModal,
                                params: {
                                    clientId: String(client.id),
                                    ...route.params,
                                },
                            }}
                        >
                            {TabContent ? <TabContent /> : null}
                        </ProntuarioDataProvider>
                    )}
                </div>
            </div>
        </div>
    )
}
